import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  Divider,
  CircularProgress,
  styled
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';
import { useTranslation } from 'react-i18next';

const CHART_COLORS = ['#1976D2', '#4CAF50', '#FF9800', '#9C27B0', '#E91E63', '#00BCD4', '#F44336', '#795548', '#607D8B', '#CDDC39'];

const ChartContainer = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
  borderRadius: 8,
  '& .MuiTypography-h6': {
    fontSize: '1rem',
    fontWeight: 'bold',
  },
}));

interface ProductSales {
  itemId: string;
  itemName: string;
  totalSales: number;
}

interface BestSellingProductsChartProps {
  data: ProductSales[];
  loading: boolean;
  /** Varsayılan "Ürün Satış Dağılımı" başlığı yerine özel başlık (ekran bazlı set için) */
  title?: string;
  /** Başlık satırının sağına özel aksiyon (örn. ekran seçimi ikonu) */
  headerAction?: React.ReactNode;
}

const BestSellingProductsChart: React.FC<BestSellingProductsChartProps> = ({ data, loading, title, headerAction }) => {
  const { t, i18n } = useTranslation('accounting');
  const locale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'tr-TR';

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => b.totalSales - a.totalSales);
  }, [data]);

  const topProduct = sorted.length > 0 ? sorted[0] : null;

  const chartData = useMemo(() => {
    if (sorted.length === 0) return [];

    const maxItems = 8;

    if (sorted.length <= maxItems) {
      return sorted.map(p => ({
        name: p.itemName.length > 16 ? `${p.itemName.substring(0, 16)}...` : p.itemName,
        fullName: p.itemName,
        value: p.totalSales,
      }));
    }

    const top = sorted.slice(0, maxItems);
    const rest = sorted.slice(maxItems);
    const otherTotal = rest.reduce((sum, p) => sum + p.totalSales, 0);

    return [
      ...top.map(p => ({
        name: p.itemName.length > 16 ? `${p.itemName.substring(0, 16)}...` : p.itemName,
        fullName: p.itemName,
        value: p.totalSales,
      })),
      {
        name: t('dashboard.otherLabel'),
        fullName: t('dashboard.otherProductsLabel', { count: rest.length }),
        value: otherTotal
      },
    ];
  }, [sorted, t]);

  const total = chartData.reduce((sum, item) => sum + item.value, 0);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const item = payload[0].payload;
      const percent = total > 0 ? ((item.value / total) * 100).toFixed(1) : 0;
      return (
        <Box sx={{ bgcolor: 'background.paper', p: 2, boxShadow: 3, borderRadius: 1 }}>
          <Typography variant="body2" fontWeight="bold">{item.fullName}</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
            <Box sx={{ width: 10, height: 10, bgcolor: payload[0].color, borderRadius: '50%' }} />
            <Typography variant="body2">{item.value.toLocaleString(locale)} {t('dashboard.piece')} (%{percent})</Typography>
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <ChartContainer>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h6" fontWeight="bold">
          {title ?? t('dashboard.bestSellingProductsChartTitle')}
        </Typography>
        {headerAction}
      </Box>
      <Divider sx={{ mb: 1 }} />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 160 }}>
          <CircularProgress />
        </Box>
      ) : chartData.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: 160 }}>
          <Typography variant="body2" color="textSecondary">{t('dashboard.chartNoData')}</Typography>
        </Box>
      ) : (
        <>
          {/* Esnek yükseklik: kart büyütülünce grafik de büyür */}
          <Box sx={{ flexGrow: 1, minHeight: 160 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius="70%"
                fill="#8884d8"
                dataKey="value"
                labelLine={false}
                label={false}
              >
                {chartData.map((_entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <RechartsTooltip content={<CustomTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          </Box>

          {topProduct && (
            <Box sx={{
              mt: 1.5,
              p: 1.5,
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}>
              <EmojiEventsIcon sx={{ color: '#FF9800', fontSize: 28 }} />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="caption" color="textSecondary">{t('dashboard.topSellingProductLabel')}</Typography>
                <Typography variant="body2" fontWeight="bold" noWrap>
                  {topProduct.itemName}
                </Typography>
                <Typography variant="caption" color="primary">
                  {topProduct.totalSales.toLocaleString(locale)} {t('dashboard.piece')}
                  {total > 0 && ` (%${((topProduct.totalSales / total) * 100).toFixed(0)})`}
                </Typography>
              </Box>
            </Box>
          )}
        </>
      )}
    </ChartContainer>
  );
};

export default BestSellingProductsChart;
